import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import {
  ShoppingBag, Heart, User, SignOut, House, Package,
  Envelope, Phone, List, X, BellSimple, BellSimpleRinging
} from '@phosphor-icons/react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const StoreAccountLayout = ({ children, storeSlug: propStoreSlug }) => {
  const { user, logout } = useAuth();
  const { storeSlug: paramStoreSlug } = useParams();
  const storeSlug = propStoreSlug || paramStoreSlug;
  const standalone = !!propStoreSlug || !paramStoreSlug;
  const location = useLocation();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const slug = storeSlug || 'magere-viandes';
    axios.get(`${API}/api/s/${slug}`).then(r => setStore(r.data)).catch(() => {});
  }, [storeSlug]);

  const handleLogout = async () => {
    await logout();
    navigate(standalone ? '/login' : `/store/${storeSlug}/login`);
  };

  const id = store?.identity || {};
  const colors = { primary: id.primary_color || '#c2185b' };
  const isMagere = store?.identity?.theme_template === 'magere' || standalone || storeSlug === 'magere-viandes';
  const loyaltyEnabled = store?.loyalty_enabled !== false; // default to true if unknown
  const commercial = store?.commercial;

  const { pushEnabled, pushSupported, subscribePush, unsubscribePush } = useNotifications();

  // Helper for standalone links
  const getLink = (path) => standalone ? path : `/store/${storeSlug}${path}`;

  const accountNav = [
    { path: getLink('/account'), label: 'Mon espace', icon: House },
    { path: getLink('/account/orders'), label: 'Mes commandes', icon: ShoppingBag },
    ...(loyaltyEnabled ? [{ path: getLink('/account/loyalty'), label: 'Fidélité', icon: Package }] : []),
    { path: getLink('/account/wishlist'), label: 'Mes favoris', icon: Heart },
    { path: getLink('/account/profile'), label: 'Mon profil', icon: User },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-[60vh]" data-testid="store-account-layout" style={{ fontFamily: "'Jost', sans-serif" }}>
      <div className="max-w-[1300px] mx-auto px-4 md:px-6 py-8">
        <div className="flex gap-6 md:gap-8">
          {/* Sidebar - Desktop */}
          <aside className="hidden md:block w-[240px] shrink-0">
            <div className="sticky top-24 space-y-0">
              {/* Nav card */}
              <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-slate-100">
                  <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Mon compte</p>
                  <p className="text-[14px] font-bold text-slate-800 mt-0.5">Bonjour {(user?.name || user?.email || '').split(' ')[0]}</p>
                </div>
                <nav className="p-2">
                  <ul className="space-y-0.5">
                    {accountNav.map(item => (
                      <li key={item.path}>
                        <Link to={item.path}
                          className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] font-medium transition-colors ${isActive(item.path) ? 'text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                          style={isActive(item.path) ? { backgroundColor: colors.primary } : {}}
                          data-testid={`account-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                          <item.icon size={18} weight={isActive(item.path) ? 'fill' : 'regular'} /> {item.label}
                        </Link>
                      </li>
                    ))}
                    <li>
                      <button onClick={handleLogout} className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] font-medium text-red-500 hover:bg-red-50 w-full">
                        <SignOut size={18} /> Déconnexion
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>

              {/* Push notification toggle */}
              {pushSupported && (
                <div className="bg-white rounded-xl border border-slate-100 shadow-sm mt-4 p-4" data-testid="push-notification-toggle">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {pushEnabled
                        ? <BellSimpleRinging size={18} weight="fill" style={{ color: colors.primary }} />
                        : <BellSimple size={18} className="text-slate-400" />
                      }
                      <span className="text-[13px] font-medium text-slate-700">Notifications push</span>
                    </div>
                    <button
                      onClick={pushEnabled ? unsubscribePush : subscribePush}
                      className={`px-3 py-1.5 rounded-full text-[11px] font-bold transition-all ${pushEnabled ? 'bg-emerald-100 text-emerald-700' : 'text-white'}`}
                      style={!pushEnabled ? { backgroundColor: colors.primary } : {}}
                      data-testid="push-toggle-btn"
                    >
                      {pushEnabled ? 'Activées' : 'Activer'}
                    </button>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1.5">
                    {pushEnabled ? 'Vous recevrez des alertes pour chaque étape de vos commandes' : 'Soyez informé en temps réel de l\'avancement de vos commandes'}
                  </p>
                </div>
              )}

              {/* Account manager card removed - now managed via /admin/commerciaux */}
              {commercial && (commercial.name || commercial.email || commercial.phone) && (
                <div className="bg-white rounded-xl border border-slate-100 shadow-sm mt-4 p-4" data-testid="commercial-card">
                  <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-3">
                    {commercial.title || 'Votre conseiller'}
                  </p>
                  <div className="flex items-center gap-3 mb-3">
                    {commercial.avatar_url ? (
                      <img src={commercial.avatar_url} alt={commercial.name}
                        className="w-12 h-12 rounded-full object-cover border-2"
                        style={{ borderColor: colors.primary }} />
                    ) : (
                      <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                        style={{ backgroundColor: colors.primary }}>
                        {(commercial.first_name || commercial.name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-[14px] font-bold text-slate-800 truncate" data-testid="commercial-name">
                        {commercial.first_name || commercial.name}
                      </p>
                      {commercial.last_name && (
                        <p className="text-[12px] text-slate-500 truncate">{commercial.last_name}</p>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1.5 pt-2 border-t border-slate-100">
                    {commercial.phone && (
                      <a href={`tel:${commercial.phone}`} className="flex items-center gap-2 text-[12px] text-slate-600 hover:text-slate-900 transition-colors" data-testid="commercial-phone">
                        <Phone size={14} className="shrink-0" style={{ color: colors.primary }} />
                        <span className="truncate">{commercial.phone}</span>
                      </a>
                    )}
                    {commercial.email && (
                      <a href={`mailto:${commercial.email}`} className="flex items-center gap-2 text-[12px] text-slate-600 hover:text-slate-900 transition-colors" data-testid="commercial-email">
                        <Envelope size={14} className="shrink-0" style={{ color: colors.primary }} />
                        <span className="truncate">{commercial.email}</span>
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </aside>

          {/* Mobile account bar */}
          <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-lg">
            <div className="flex justify-around py-2">
              {accountNav.map(item => (
                <Link key={item.path} to={item.path}
                  className={`flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] font-medium ${isActive(item.path) ? '' : 'text-slate-400'}`}
                  style={isActive(item.path) ? { color: colors.primary } : {}}>
                  <item.icon size={20} weight={isActive(item.path) ? 'fill' : 'regular'} />
                  {item.label.replace('Mes ', '').replace('Mon ', '')}
                </Link>
              ))}
            </div>
          </div>

          {/* Main content */}
          <main className="flex-1 min-w-0 pb-20 md:pb-0">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
};

export default StoreAccountLayout;
